import { useState, useEffect, useCallback } from 'react';
import DeleteReferenceModal from './DeleteReferenceModal/index';
import { referenceTracker } from 'v3/quantConfig/reference/referenceTracker';
import useReference from 'v2/modules/withRunForm/hooks/useReference/index';
import { BaseReference, REFERENCE_LIST, ReferenceEntity } from '../types/reference.type';
import _ from 'lodash';
import bootstrapGrowl from 'common/configs/bootstrapGrowl';

const ReferencesModalContainer = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<{
    entity: ReferenceEntity;
    references: BaseReference[];
  } | null>(null);

  const { getAllIntentReference, getAllCaseReference, getAllPipeReference } = useReference();

  const getReferences = useCallback(
    (entity: ReferenceEntity): BaseReference[] | [] => {
      switch (entity.type) {
        case REFERENCE_LIST.pipe: {
          return getAllPipeReference(entity.index);
        }
        case REFERENCE_LIST.intent: {
          const { identifier: { caseIndex, label: intentIndex } = {} } = entity.details;
          return getAllIntentReference(caseIndex, intentIndex);
        }
        case REFERENCE_LIST.case: {
          return getAllCaseReference(entity.index);
        }
        case REFERENCE_LIST.adjustment: {
          const { intents = [] } = entity.details;
          const adjustmentReference: BaseReference[] = [];
          if (intents.length > 0) {
            _.forEach(intents, (intent) => {
              const { identifier: { caseIndex, label: intentIndex } = {} } = intent;
              adjustmentReference.push(...getAllIntentReference(caseIndex, intentIndex));
            });
          }
          return adjustmentReference;
        }
        default:
          return [];
      }
    },
    [getAllCaseReference, getAllIntentReference, getAllPipeReference]
  );

  const handleDeleteOrShowWarning = useCallback(
    (entity: ReferenceEntity, onDelete: () => void) => {
      const references = getReferences(entity);
      if (references?.length > 0) {
        // TODO: Uncomment the below logic when Expression is completed
        // setShowModal(true);
        let referenceMessage = 'Please remove the usage and then delete this item.';
        if (entity.type === REFERENCE_LIST.pipe) {
          referenceMessage = 'Please remove the usage and then delete this indicator.';
        }
        if (entity.type === REFERENCE_LIST.case) {
          referenceMessage = `Please remove the usage and then delete this Case ${entity.index + 1}.`;
        }
        if (entity.type === REFERENCE_LIST.intent) {
          const {
            identifier: { label: indentIndex },
          } = entity.details;
          referenceMessage = `Please remove the usage and then delete this Leg ${indentIndex}.`;
        }
        if (entity.type === REFERENCE_LIST.adjustment) {
          referenceMessage = `Please remove the usage and then delete this Adjustment ${entity.index + 1}.`;
        }
        bootstrapGrowl(referenceMessage, {
          type: 'warning',
          allow_dismiss: true,
          align: 'center',
        });
        setModalData({ entity, references });
      } else {
        onDelete();
      }
    },
    [getReferences]
  );

  const handleCheckReferencePresent = useCallback(
    (entity: ReferenceEntity): boolean => {
      const references = getReferences(entity);
      return references?.length > 0;
    },
    [getReferences]
  );

  useEffect(() => {
    referenceTracker.performDeleteOrShowWarning = handleDeleteOrShowWarning;
    return () => {
      referenceTracker.performDeleteOrShowWarning = () => {};
    };
  }, [handleDeleteOrShowWarning]);

  useEffect(() => {
    referenceTracker.checkReferencePresent = handleCheckReferencePresent;
    return () => {
      referenceTracker.checkReferencePresent = () => false;
    };
  }, [handleCheckReferencePresent]);

  return <>{showModal && <DeleteReferenceModal onClose={() => setShowModal(false)} {...modalData} />}</>;
};

export default ReferencesModalContainer;
